/**
 * TSG Nexus — the thin client the REUSED learner components use.
 *
 * ExplainPage / MockInterview / EliteTechnologyMastery & friends render in
 * THREE worlds (learner app, college portals, TSG Nexus). Only TSG Nexus
 * builders are metered by feature billing, so every helper here is a no-op
 * unless BOTH are true:
 *
 *   1. the signed-in account is a Nexus builder (isNexusBuilderSession), and
 *   2. we are actually inside the /nextgen-builder/* layout.
 *
 * (2) is what keeps tenant/college portals byte-for-byte unchanged even when
 * a builder happens to be signed in.
 */
import axios from 'axios';
import API_BASE_URL from '../../Config/config';
import { isNexusBuilderSession, nexusLearnerHeaders } from './billingApi';

/** True only for a Nexus builder inside the TSG Nexus layout. */
export const isNexusBuilderUi = () => {
  try {
    if (typeof window === 'undefined') return false;
    if (!window.location.pathname.startsWith('/nextgen-builder')) return false;
    return isNexusBuilderSession();
  } catch (_) {
    return false;
  }
};

/** Idempotency key for one prompt/generation so a backend retry can't double-charge. */
export const newRequestId = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch (_) { /* fall through */ }
  return `req-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
};

/**
 * Headers a metered learner request carries in a Nexus session.
 * Returns `{}` everywhere else, so spreading it is always safe.
 */
export const nexusRequestHeaders = () => (isNexusBuilderUi() ? { ...nexusLearnerHeaders(), 'X-Request-Id': newRequestId() } : {});

/**
 * Read the `x-nexus-usage` header off a fetch Response OR an axios response.
 * Informational only — the on-page counters are refreshed from the server
 * (refreshSummary) because that is cheap and always correct.
 */
export const readNexusUsageHeader = (res) => {
  try {
    const h = res && res.headers;
    if (!h) return null;
    const raw = typeof h.get === 'function' ? h.get('x-nexus-usage') : (h['x-nexus-usage'] || h['X-Nexus-Usage']);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

/** Elite Mastery gated-reading endpoints (Nexus only). */
export const eliteAccessApi = {
  /** Access map for one technology: which pages are FREE / UNLOCKED / AVAILABLE / LOCKED. */
  getPages: async (slug) => {
    const res = await axios.get(`${API_BASE_URL}/api/elite/pages/${encodeURIComponent(slug)}`, { headers: nexusLearnerHeaders() });
    return res.data && res.data.data ? res.data.data : res.data;
  },
  /** Server-rendered markdown for one page. Throws an axios 402 when a purchase is required. */
  getContent: async (slug, filePath) => {
    const res = await axios.get(`${API_BASE_URL}/api/elite/content`, {
      params: { technology: slug, path: filePath },
      headers: nexusLearnerHeaders(),
    });
    return res.data && res.data.data ? res.data.data : res.data;
  },
};

const nexusLearnerApi = { isNexusBuilderUi, newRequestId, nexusRequestHeaders, readNexusUsageHeader, eliteAccessApi };
export default nexusLearnerApi;
